import type {
  ElectionResults,
  ElectionResultWithDetails,
  NationalElectionSummary,
  PartyResultComparison,
} from '@/types'

// ============================================================================
// Types
// ============================================================================

export interface BattlegroundDistrict {
  districtId: string
  districtName: string
  regionName: string
  winnerPartyId: string
  winnerPartyName: string
  winnerPartyColor: string
  runnerUpPartyId: string
  runnerUpPartyName: string
  runnerUpPartyColor: string
  winnerVoteShare: number
  runnerUpVoteShare: number
  margin: number
  flipped: boolean
}

export interface NarrativeSummary {
  sentences: string[]
}

export interface BiggestMover {
  partyName: string
  partyColor: string
  seatChange: number
  direction: 'gained' | 'lost'
}

export interface ThresholdCasualty {
  partyName: string
  partyColor: string
  voteShare: number
}

// ============================================================================
// Battleground Identification
// ============================================================================

/**
 * Identifies battleground districts where the margin between 1st and 2nd place
 * was within the threshold. Optionally detects flipped districts if previous
 * election results are provided.
 */
export function identifyBattlegrounds(
  results: ElectionResultWithDetails[],
  previousResults: ElectionResultWithDetails[] | undefined,
  marginThreshold = 5,
  limit = 5,
): BattlegroundDistrict[] {
  // Filter to district-level results only
  const districtResults = results.filter((r) => r.district_id != null)

  // Group by district
  const byDistrict = new Map<string, ElectionResultWithDetails[]>()
  for (const r of districtResults) {
    const id = r.district_id!
    if (!byDistrict.has(id)) byDistrict.set(id, [])
    byDistrict.get(id)!.push(r)
  }

  // Build previous winner map for flip detection
  const previousWinners = new Map<string, string>()
  if (previousResults) {
    const prevDistrict = previousResults.filter((r) => r.district_id != null)
    const prevByDistrict = new Map<string, ElectionResultWithDetails[]>()
    for (const r of prevDistrict) {
      const id = r.district_id!
      if (!prevByDistrict.has(id)) prevByDistrict.set(id, [])
      prevByDistrict.get(id)!.push(r)
    }
    for (const [districtId, dResults] of prevByDistrict) {
      const sorted = [...dResults].sort((a, b) => b.vote_share - a.vote_share)
      if (sorted.length > 0) {
        previousWinners.set(districtId, sorted[0].party_id)
      }
    }
  }

  const battlegrounds: BattlegroundDistrict[] = []

  for (const [districtId, dResults] of byDistrict) {
    if (dResults.length < 2) continue

    const sorted = [...dResults].sort((a, b) => b.vote_share - a.vote_share)
    const winner = sorted[0]
    const runnerUp = sorted[1]
    const margin = winner.vote_share - runnerUp.vote_share

    if (margin >= marginThreshold) continue

    const prevWinner = previousWinners.get(districtId)
    const flipped = prevWinner != null && prevWinner !== winner.party_id

    battlegrounds.push({
      districtId,
      districtName: winner.district?.name ?? 'Unknown District',
      regionName: winner.region?.name ?? winner.district?.region_id ?? '',
      winnerPartyId: winner.party_id,
      winnerPartyName: winner.party?.name ?? 'Unknown',
      winnerPartyColor: winner.party?.color ?? '#888888',
      runnerUpPartyId: runnerUp.party_id,
      runnerUpPartyName: runnerUp.party?.name ?? 'Unknown',
      runnerUpPartyColor: runnerUp.party?.color ?? '#888888',
      winnerVoteShare: winner.vote_share,
      runnerUpVoteShare: runnerUp.vote_share,
      margin,
      flipped,
    })
  }

  // Sort by margin ascending (closest first) and take top N
  battlegrounds.sort((a, b) => a.margin - b.margin)
  return battlegrounds.slice(0, limit)
}

// ============================================================================
// Analysis Helpers
// ============================================================================

/**
 * Finds the party with the biggest absolute seat change.
 */
export function findBiggestMover(
  comparison: PartyResultComparison[],
): BiggestMover | null {
  if (comparison.length === 0) return null

  let biggest: PartyResultComparison | null = null
  let maxAbsChange = 0

  for (const party of comparison) {
    const abs = Math.abs(party.seat_change)
    if (abs > maxAbsChange) {
      maxAbsChange = abs
      biggest = party
    }
  }

  if (!biggest || maxAbsChange === 0) return null

  return {
    partyName: biggest.party_name,
    partyColor: biggest.party_color,
    seatChange: Math.abs(biggest.seat_change),
    direction: biggest.seat_change > 0 ? 'gained' : 'lost',
  }
}

/**
 * Returns parties whose vote share fell below the electoral threshold
 * but who still ran (had votes). Parties with 0% threshold are excluded.
 */
export function findThresholdCasualties(
  summary: NationalElectionSummary,
  thresholdPercent: number,
): ThresholdCasualty[] {
  if (thresholdPercent <= 0) return []

  return summary.party_results
    .filter((p) => p.vote_share > 0 && p.vote_share < thresholdPercent && p.seats_won === 0)
    .map((p) => ({
      partyName: p.party_name,
      partyColor: p.party_color,
      voteShare: p.vote_share,
    }))
}

// ============================================================================
// Narrative Generation
// ============================================================================

/**
 * Generates a 3-5 sentence narrative summary of the election outcome.
 */
export function generateNarrative(
  electionResults: ElectionResults,
  summary: NationalElectionSummary,
  comparison: PartyResultComparison[] | undefined,
  battlegrounds: BattlegroundDistrict[],
  thresholdPercent: number,
  hasPreviousElection: boolean,
  nationName: string,
): NarrativeSummary {
  const sentences: string[] = []

  // Sentence 1: Overall winner + majority status
  const winnerParty = summary.party_results.find(
    (p) => p.party_id === electionResults.winner_party_id,
  )
  if (winnerParty) {
    const majorityWord = electionResults.majority_achieved
      ? 'a commanding majority'
      : 'the most seats but fell short of a majority'
    sentences.push(
      `The ${winnerParty.party_name} won ${majorityWord} with ${winnerParty.seats_won} of ${summary.total_seats} seats.`,
    )
  } else {
    sentences.push(
      `No party emerged as a clear winner in this election across ${summary.total_seats} seats.`,
    )
  }

  // Sentence 2: Biggest mover or first election note
  if (hasPreviousElection && comparison && comparison.length > 0) {
    const mover = findBiggestMover(comparison)
    if (mover) {
      sentences.push(
        `The ${mover.partyName} saw the biggest shift, ${mover.direction === 'gained' ? 'gaining' : 'losing'} ${mover.seatChange} seat${mover.seatChange !== 1 ? 's' : ''} compared to the previous election.`,
      )
    }
  } else {
    sentences.push(`This was the first election held in ${nationName}.`)
  }

  // Sentence 3: Closest race
  if (battlegrounds.length > 0) {
    const closest = battlegrounds[0]
    sentences.push(
      `The tightest contest was in ${closest.districtName}, where the ${closest.winnerPartyName} edged out the ${closest.runnerUpPartyName} by just ${closest.margin.toFixed(1)}%.`,
    )
  }

  // Sentence 4: Flipped districts (if any)
  if (hasPreviousElection) {
    const flipped = battlegrounds.filter((b) => b.flipped)
    if (flipped.length > 0) {
      const districtNames = flipped.slice(0, 3).map((b) => b.districtName)
      const suffix = flipped.length > 3 ? ` and ${flipped.length - 3} more` : ''
      sentences.push(
        `${districtNames.join(', ')}${suffix} flipped to a different party.`,
      )
    }
  }

  // Sentence 5: Threshold casualties (if any)
  const casualties = findThresholdCasualties(summary, thresholdPercent)
  if (casualties.length > 0) {
    const names = casualties.map((c) => `the ${c.partyName}`).join(' and ')
    sentences.push(
      `${names} fell below the ${thresholdPercent}% threshold and received no seats.`,
    )
  }

  return { sentences }
}
